import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Badge, Modal, ModalHeader, ModalBody, ModalFooter, Label, Input, Form, FormFeedback } from 'reactstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ticketRequestsAPI, usersAPI } from '../../services/api';
import AlertModal from '../../components/Common/AlertModal';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';

const TicketRequestsList = () => {
    document.title = "Demandes de tickets | Ticket Management";

    const { user } = useAuth();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [approveModal, setApproveModal] = useState(false);
    const [rejectModal, setRejectModal] = useState(false);
    const [users, setUsers] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [alertConfig, setAlertConfig] = useState({
        isOpen: false,
        type: 'success',
        message: '',
        title: ''
    });

    const toggleAlert = () => setAlertConfig({ ...alertConfig, isOpen: !alertConfig.isOpen });

    const isAdmin = user?.role === 'admin' || user?.role === 'manager';
    const isClient = user?.role === 'client';

    useEffect(() => {
        fetchRequests();
        if (isAdmin) {
            fetchUsers();
        }
    }, []);

    const fetchRequests = async () => {
        try {
            const response = await ticketRequestsAPI.getAll();
            setRequests(response.data);
        } catch (error) {
            console.error('Erreur lors du chargement des demandes:', error);
            toast.error('Erreur lors du chargement des demandes');
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await usersAPI.getAll();
            setUsers(response.data);
        } catch (error) {
            console.error('Erreur lors du chargement des utilisateurs:', error);
        }
    };

    const approveValidation = useFormik({
        enableReinitialize: true,
        initialValues: {
            assigned_to: '',
            heures_estimees: '',
            deadline: '',
        },
        validationSchema: Yup.object({
            assigned_to: Yup.string().required('Veuillez assigner le ticket'),
            heures_estimees: Yup.number().min(1, 'Minimum 1 heure'),
            deadline: Yup.date(),
        }),
        onSubmit: async (values) => {
            setIsSubmitting(true);
            try {
                await ticketRequestsAPI.approve(selectedRequest.id, values);
                setAlertConfig({
                    isOpen: true,
                    type: 'success',
                    title: 'Approuvé',
                    message: 'La demande a été approuvée et le ticket a été créé avec succès.'
                });
                setApproveModal(false);
                fetchRequests();
            } catch (error) {
                setAlertConfig({
                    isOpen: true,
                    type: 'error',
                    title: 'Erreur',
                    message: "Une erreur est survenue lors de l'approbation de la demande."
                });
            } finally {
                setIsSubmitting(false);
            }
        },
    });

    const rejectValidation = useFormik({
        enableReinitialize: true,
        initialValues: {
            raison_rejet: '',
        },
        validationSchema: Yup.object({
            raison_rejet: Yup.string().required('Veuillez indiquer la raison du rejet'),
        }),
        onSubmit: async (values) => {
            setIsSubmitting(true);
            try {
                await ticketRequestsAPI.reject(selectedRequest.id, values);
                setAlertConfig({
                    isOpen: true,
                    type: 'success',
                    title: 'Rejeté',
                    message: 'La demande a été rejetée avec succès.'
                });
                setRejectModal(false);
                fetchRequests();
            } catch (error) {
                setAlertConfig({
                    isOpen: true,
                    type: 'error',
                    title: 'Erreur',
                    message: "Une erreur est survenue lors du rejet de la demande."
                });
            } finally {
                setIsSubmitting(false);
            }
        },
    });

    const getStatutBadge = (statut) => {
        const badges = {
            en_attente: { color: 'warning', icon: 'bx-time', text: 'En attente' },
            approuve: { color: 'success', icon: 'bx-check-circle', text: 'Approuvé' },
            rejete: { color: 'danger', icon: 'bx-x-circle', text: 'Rejeté' },
        };
        const badge = badges[statut] || badges.en_attente;
        return (
            <Badge color={badge.color} className="font-size-12">
                <i className={`bx ${badge.icon} me-1`}></i>
                {badge.text}
            </Badge>
        );
    };

    const getPrioriteBadge = (priorite) => {
        const priorities = {
            urgente: 'danger',
            haute: 'warning',
            normale: 'info',
            basse: 'secondary',
        };
        return (
            <Badge color={priorities[priorite]} className="font-size-11">
                {priorite.charAt(0).toUpperCase() + priorite.slice(1)}
            </Badge>
        );
    };

    const handleApprove = (request) => {
        setSelectedRequest(request);
        setApproveModal(true);
    };

    const handleReject = (request) => {
        setSelectedRequest(request);
        setRejectModal(true);
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row className="mb-3">
                        <Col>
                            {isClient && (
                                <Link to="/create-ticket-request">
                                    <Button color="primary">
                                        <i className="bx bx-plus me-1"></i>
                                        Nouvelle demande
                                    </Button>
                                </Link>
                            )}
                        </Col>
                    </Row>

                    <Row>
                        {loading ? (
                            <Col>
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Chargement...</span>
                                    </div>
                                </div>
                            </Col>
                        ) : requests.length === 0 ? (
                            <Col>
                                <Card>
                                    <CardBody className="text-center py-5">
                                        <i className="bx bx-file display-4 text-muted"></i>
                                        <h5 className="mt-3">Aucune demande</h5>
                                        {isClient && (
                                            <Link to="/create-ticket-request">
                                                <Button color="primary" className="mt-2">
                                                    Créer votre première demande
                                                </Button>
                                            </Link>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>
                        ) : (
                            <Col>
                                <Card>
                                    <CardBody>
                                        <div className="table-responsive">
                                            <table className="table table-hover mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Titre</th>
                                                        {!isClient && <th>Client</th>}
                                                        <th>Projet</th>
                                                        <th>Priorité</th>
                                                        <th>Statut</th>
                                                        <th>Date</th>
                                                        {isAdmin && <th>Actions</th>}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {requests.map((request) => (
                                                        <tr key={request.id}>
                                                            <td>{request.id}</td>
                                                            <td>
                                                                <strong>{request.titre}</strong>
                                                                <br />
                                                                <small className="text-muted">
                                                                    {request.description.substring(0, 50)}...
                                                                </small>
                                                            </td>
                                                            {!isClient && (
                                                                <td>{request.client?.nom}</td>
                                                            )}
                                                            <td>{request.projet?.nom}</td>
                                                            <td>{getPrioriteBadge(request.priorite)}</td>
                                                            <td>{getStatutBadge(request.statut)}</td>
                                                            <td>
                                                                <small className="text-muted">
                                                                    {new Date(request.created_at).toLocaleDateString('fr-FR')}
                                                                </small>
                                                            </td>
                                                            {isAdmin && (
                                                                <td>
                                                                    {request.statut === 'en_attente' ? (
                                                                        <div className="d-flex gap-2">
                                                                            <Button
                                                                                color="success"
                                                                                size="sm"
                                                                                onClick={() => handleApprove(request)}
                                                                            >
                                                                                <i className="bx bx-check"></i>
                                                                            </Button>
                                                                            <Button
                                                                                color="danger"
                                                                                size="sm"
                                                                                onClick={() => handleReject(request)}
                                                                            >
                                                                                <i className="bx bx-x"></i>
                                                                            </Button>
                                                                        </div>
                                                                    ) : request.ticket_id ? (
                                                                        <Link to={`/tickets/${request.ticket_id}`}>
                                                                            <Button color="info" size="sm">
                                                                                Voir ticket
                                                                            </Button>
                                                                        </Link>
                                                                    ) : (
                                                                        <Badge color="secondary">Traité</Badge>
                                                                    )}
                                                                </td>
                                                            )}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        )}
                    </Row>
                </Container>
            </div>

            {/* Modal Approuver */}
            <Modal isOpen={approveModal} toggle={() => setApproveModal(false)}>
                <ModalHeader toggle={() => setApproveModal(false)}>
                    Approuver la demande
                </ModalHeader>
                <ModalBody>
                    <Form onSubmit={approveValidation.handleSubmit}>
                        <div className="mb-3">
                            <Label>Assigner à</Label>
                            <Input
                                type="select"
                                name="assigned_to"
                                value={approveValidation.values.assigned_to}
                                onChange={approveValidation.handleChange}
                                onBlur={approveValidation.handleBlur}
                                invalid={approveValidation.touched.assigned_to && approveValidation.errors.assigned_to}
                            >
                                <option value="">Sélectionnez un utilisateur</option>
                                {users.map((user) => (
                                    <option key={user.id} value={user.id}>
                                        {user.name} ({user.role})
                                    </option>
                                ))}
                            </Input>
                            {approveValidation.touched.assigned_to && approveValidation.errors.assigned_to && (
                                <FormFeedback type="invalid" style={{ display: 'block' }}>
                                    {approveValidation.errors.assigned_to}
                                </FormFeedback>
                            )}
                        </div>

                        <div className="mb-3">
                            <Label>Heures estimées</Label>
                            <Input
                                type="number"
                                name="heures_estimees"
                                value={approveValidation.values.heures_estimees}
                                onChange={approveValidation.handleChange}
                                onBlur={approveValidation.handleBlur}
                            />
                        </div>

                        <div className="mb-3">
                            <Label>Date limite (optionnel)</Label>
                            <Input
                                type="date"
                                name="deadline"
                                value={approveValidation.values.deadline}
                                onChange={approveValidation.handleChange}
                                onBlur={approveValidation.handleBlur}
                            />
                        </div>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setApproveModal(false)} disabled={isSubmitting}>
                        Annuler
                    </Button>
                    <Button color="success" onClick={approveValidation.handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <i className="bx bx-loader bx-spin me-1"></i> Approbation...
                            </>
                        ) : "Approuver et créer le ticket"}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Modal Rejeter */}
            <Modal isOpen={rejectModal} toggle={() => setRejectModal(false)}>
                <ModalHeader toggle={() => setRejectModal(false)}>
                    Rejeter la demande
                </ModalHeader>
                <ModalBody>
                    <Form onSubmit={rejectValidation.handleSubmit}>
                        <div className="mb-3">
                            <Label>Raison du rejet</Label>
                            <Input
                                type="textarea"
                                name="raison_rejet"
                                rows="4"
                                value={rejectValidation.values.raison_rejet}
                                onChange={rejectValidation.handleChange}
                                onBlur={rejectValidation.handleBlur}
                                invalid={rejectValidation.touched.raison_rejet && rejectValidation.errors.raison_rejet}
                            />
                            {rejectValidation.touched.raison_rejet && rejectValidation.errors.raison_rejet && (
                                <FormFeedback>{rejectValidation.errors.raison_rejet}</FormFeedback>
                            )}
                        </div>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setRejectModal(false)} disabled={isSubmitting}>
                        Annuler
                    </Button>
                    <Button color="danger" onClick={rejectValidation.handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <i className="bx bx-loader bx-spin me-1"></i> Rejet...
                            </>
                        ) : "Rejeter"}
                    </Button>
                </ModalFooter>
            </Modal>

            <AlertModal
                isOpen={alertConfig.isOpen}
                toggle={toggleAlert}
                type={alertConfig.type}
                title={alertConfig.title}
                message={alertConfig.message}
            />
        </React.Fragment>
    );
};

export default TicketRequestsList;
